import {
	type ProductOption,
	type Product,
	type ProductImage,
	type ProductOptionValue,
	type ProductVariant,
} from "@prisma/client"
import { Link, useFetcher, useRevalidator } from "@remix-run/react"
import { useState } from "react"
import LoaderImage from "#app/components/loader-image"
import { Button } from "#app/components/ui/button"
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
	DrawerTrigger,
} from "#app/components/ui/drawer"
import { Separator } from "#app/components/ui/separator"
import {
	TypographyH1,
	TypographyLarge,
	TypographyP,
} from "#app/components/ui/typography"
import { addToCartIntent } from "#app/routes/_marketing+/cart"
import { getProductImgSrc, price } from "#app/utils/misc"
import { useOptionalUser, userHasRole } from "#app/utils/user"
import routes from "#routes"

const AddToCart = ({
	product,
	amountInCart,
}: {
	product: Product & {
		options: (ProductOption & { values: ProductOptionValue[] })[]
		variants: (ProductVariant & { options: ProductOptionValue[] })[]
		images: Pick<ProductImage, "id" | "altText">[]
	}
	amountInCart?: number
}) => {
	const fetcher = useFetcher()
	const revalidator = useRevalidator()
	const [variantId, setVariantId] = useState<string | undefined>()

	// Si el producto no tiene variantes, no se necesita hacer nada especial.
	if (product.variants.length === 0) {
		return (
			<fetcher.Form
				onSubmit={() => {
					revalidator.revalidate()
				}}
				action="/cart"
				method="POST"
			>
				<input type="hidden" name="productId" value={product.id} />
				<Button
					className="w-full"
					disabled={product.stock === 0 || amountInCart === 1}
					name="intent"
					value={addToCartIntent}
					type="submit"
				>
					{amountInCart === 1
						? "Producto en tu carrito"
						: fetcher.state === "idle"
							? "Añadir al carrito"
							: "Añadiendo al carrito..."}
				</Button>
			</fetcher.Form>
		)
	}

	// Si el producto tiene variantes, se necesita hacer un poco más de trabajo.
	return (
		<Drawer>
			<DrawerTrigger className="w-full">
				<Button type="button" className="w-full">
					Añadir al carrito
				</Button>
			</DrawerTrigger>
			<DrawerContent className="flex items-center justify-center">
				<fetcher.Form
					className="w-full max-w-[700px]"
					onSubmit={() => {
						console.log("Submitting")
						revalidator.revalidate()
					}}
					action="/cart"
					method="POST"
				>
					<DrawerHeader>
						<DrawerTitle>Selecciona la variante</DrawerTitle>
						<DrawerDescription>
							Selecciona las opciones para añadir el producto al carrito.
						</DrawerDescription>
					</DrawerHeader>
					<div className="mx-4 my-5 grid grid-cols-1 gap-4">
						<input type="hidden" name="productId" value={product.id} />
						<div className="flex flex-col gap-2">
							{product.variants.map(variant => (
								<label
									key={variant.id}
									className="flex flex-1 cursor-pointer select-none items-center gap-2"
								>
									<input
										disabled={variant.stock === 0}
										type="radio"
										className="peer absolute h-0 w-0 cursor-pointer opacity-0"
										name="variantId"
										value={variant.id}
										onChange={e => {
											setVariantId(variant.id)
										}}
									/>
									<span className="w-full rounded-md border px-3 py-2 text-center transition-colors duration-200 ease-in-out peer-checked:bg-secondary peer-disabled:cursor-not-allowed peer-disabled:opacity-30">
										{variant.title}
									</span>
								</label>
							))}
						</div>

						<Separator />

						<div className="text-end">
							<TypographyLarge>
								{price(
									product.variants.find(variant => variant.id === variantId)
										?.price || product.price,
								)}
							</TypographyLarge>
						</div>
					</div>
					<DrawerFooter className="flex w-full flex-col gap-2  lg:flex-row-reverse">
						<Button
							disabled={!variantId}
							name="intent"
							value={addToCartIntent}
							type="submit"
							className="w-full flex-1"
						>
							Añadir al carrito
						</Button>
						<DrawerClose className="flex-1">
							<Button variant="outline" className="w-full flex-1" type="button">
								Cancel
							</Button>
						</DrawerClose>
					</DrawerFooter>
				</fetcher.Form>
			</DrawerContent>
		</Drawer>
	)
}

export default function ProductDetail({
	showActions = false,
	amountInCart,
	...product
}: Product & {
	options: (ProductOption & { values: ProductOptionValue[] })[]
	variants: (ProductVariant & {
		options: ProductOptionValue[]
	})[]
	images: Pick<ProductImage, "id" | "altText">[]
} & (
		| { showActions?: true; amountInCart: number }
		| { showActions?: false; amountInCart?: never }
	)) {
	const user = useOptionalUser()
	const isAdmin = userHasRole(user || null, "admin")

	return (
		<div className="relative grid grid-cols-1 items-start gap-10 lg:grid-cols-[minmax(500px,_1fr)_1fr]">
			<div className="top-12 flex flex-col lg:sticky">
				<TypographyH1>{product.name}</TypographyH1>
				<TypographyP className="text-2xl">
					{product.variants.length > 0 && (
						<span className="block text-xs font-bold">desde</span>
					)}
					{product.variants.length === 0
						? price(product.price)
						: // Take the minimum price of the variants available
							price(
								product.variants.reduce(
									(min, variant) => (variant.price < min ? variant.price : min),
									product.price,
								),
							)}
				</TypographyP>
				{showActions && (
					<div className="mt-6 grid grid-cols-1 items-center gap-2 md:grid-cols-2">
						<AddToCart amountInCart={amountInCart} product={product} />

						{isAdmin && (
							<Link to={routes.admin.product.edit.route(product.id)}>
								<Button className="w-full" variant="outline">
									Editar producto
								</Button>
							</Link>
						)}
					</div>
				)}
				<article
					dangerouslySetInnerHTML={{
						__html: product.description,
					}}
					className="prose mt-6 whitespace-pre-line dark:text-gray-200"
				/>
			</div>

			<div className="flex flex-col gap-3">
				{product.images.map((image, index) => (
					<LoaderImage
						key={index}
						alt={image.altText || `${product.name} ${index + 1}`}
						src={getProductImgSrc(image.id)}
					/>
				))}
			</div>
		</div>
	)
}
